import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AppShell from '../components/AppShell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { 
  BookOpen, Play, Clock, Users, Star, Lock, Crown, 
  Search, Filter, ArrowLeft, Plus, Sparkles, ChevronRight,
  GraduationCap, Trophy, CheckCircle, Download, Trash2, RotateCcw, XSquare,
  Archive, ArchiveRestore
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tiny seeded RNG so the "discover" row order is stable for one page-load.
function mulberry32(seed) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export default function Courses() {
  const { user, getAuthHeader, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [myCourses, setMyCourses] = useState([]);
  const [myProgress, setMyProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [activeTab, setActiveTab] = useState('browse');
  // Seed kept constant per page-load so the "discover" row doesn't reshuffle
  // every render while the user types in the search box.
  const [shuffleSeed] = useState(() => Math.random());
  // Tracks the id of the course currently being deleted (disables the
  // button and shows a spinner-adjacent label while the DELETE is in-flight).
  const [deletingCourseId, setDeletingCourseId] = useState(null);
  // Multi-select bulk-archive state (My Created Courses tab).
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkArchiving, setBulkArchiving] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  // Archived tab state — populated lazily when the tab is opened.
  const [archivedCourses, setArchivedCourses] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [busyArchiveId, setBusyArchiveId] = useState(null);
  // Trash tab state — populated lazily when the tab is opened.
  const [trashedCourses, setTrashedCourses] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);
  const [busyTrashId, setBusyTrashId] = useState(null);
  const [emptyingTrash, setEmptyingTrash] = useState(false);
  // Admin-only "who deleted it" filter on the Trash tab ('all' or a user id).
  const [trashFilter, setTrashFilter] = useState('all');

  const clearSelection = () => setSelectedIds(new Set());

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedIds(new Set(myCourses.map((c) => c.id)));
  };

  const handleDeleteCourse = async (course) => {
    setDeletingCourseId(course.id);
    try {
      const headers = getAuthHeader();
      await axios.delete(`${API_URL}/api/courses/${course.id}`, { headers });
      // Optimistically drop the deleted course from both lists so the UI
      // updates instantly without a re-fetch round-trip.
      setMyCourses((prev) => prev.filter((c) => c.id !== course.id));
      setCourses((prev) => prev.filter((c) => c.id !== course.id));
      setArchivedCourses((prev) => prev.filter((c) => c.id !== course.id));
      // Also drop from the selection set in case the user was multi-selecting.
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(course.id);
        return next;
      });
      toast.success(`"${course.title}" moved to trash. You have 30 days to restore.`);
    } catch (error) {
      console.error('Delete course failed:', error);
      const detail = error?.response?.data?.detail || 'Failed to delete course';
      toast.error(detail);
    } finally {
      setDeletingCourseId(null);
    }
  };

  const handleBulkArchive = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkArchiving(true);
    try {
      const headers = getAuthHeader();
      const res = await axios.post(
        `${API_URL}/api/courses/bulk-archive`,
        { course_ids: ids },
        { headers },
      );
      const archived = res.data?.archived ?? 0;
      const skipped = res.data?.skipped?.length ?? 0;
      const skippedIds = new Set((res.data?.skipped || []).map((s) => s.course_id));
      const archivedIds = ids.filter((id) => !skippedIds.has(id));
      setMyCourses((prev) => prev.filter((c) => !archivedIds.includes(c.id)));
      setCourses((prev) => prev.filter((c) => !archivedIds.includes(c.id)));
      clearSelection();
      if (skipped > 0) {
        toast.success(`Archived ${archived}. ${skipped} skipped (not yours or already archived).`);
      } else {
        toast.success(`Archived ${archived} course${archived === 1 ? '' : 's'}. Find them under the Archived tab.`);
      }
      setBulkDialogOpen(false);
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Bulk archive failed';
      toast.error(detail);
    } finally {
      setBulkArchiving(false);
    }
  };

  const handleArchiveCourse = async (course) => {
    setBusyArchiveId(course.id);
    try {
      const headers = getAuthHeader();
      await axios.post(`${API_URL}/api/courses/${course.id}/archive`, {}, { headers });
      setMyCourses((prev) => prev.filter((c) => c.id !== course.id));
      setCourses((prev) => prev.filter((c) => c.id !== course.id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(course.id);
        return next;
      });
      toast.success(`"${course.title}" archived. Find it under the Archived tab.`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to archive course');
    } finally {
      setBusyArchiveId(null);
    }
  };

  const fetchArchived = async () => {
    setArchivedLoading(true);
    try {
      const headers = getAuthHeader();
      const res = await axios.get(`${API_URL}/api/courses/archived`, { headers });
      setArchivedCourses(res.data || []);
    } catch (error) {
      toast.error('Failed to load archived courses');
    } finally {
      setArchivedLoading(false);
    }
  };

  const handleUnarchive = async (course) => {
    setBusyArchiveId(course.id);
    try {
      const headers = getAuthHeader();
      await axios.post(`${API_URL}/api/courses/${course.id}/unarchive`, {}, { headers });
      setArchivedCourses((prev) => prev.filter((c) => c.id !== course.id));
      setMyCourses((prev) => (prev.some((c) => c.id === course.id) ? prev : [...prev, course]));
      toast.success(`"${course.title}" restored to your courses.`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to restore course');
    } finally {
      setBusyArchiveId(null);
    }
  };

  const handleEmptyTrash = async () => {
    setEmptyingTrash(true);
    try {
      const headers = getAuthHeader();
      const filtered = user?.is_admin && trashFilter !== 'all';
      const body = filtered ? { deleted_by: trashFilter } : {};
      const res = await axios.post(`${API_URL}/api/courses/trash/empty`, body, { headers });
      const purged = res.data?.purged ?? 0;
      if (filtered) {
        setTrashedCourses((prev) => prev.filter((c) => c.deleted_by !== trashFilter));
        setTrashFilter('all');
      } else {
        setTrashedCourses([]);
      }
      toast.success(`Trash emptied — ${purged} course${purged === 1 ? '' : 's'} permanently deleted.`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to empty trash');
    } finally {
      setEmptyingTrash(false);
    }
  };

  const fetchTrash = async () => {
    setTrashLoading(true);
    try {
      const headers = getAuthHeader();
      const res = await axios.get(`${API_URL}/api/courses/deleted`, { headers });
      setTrashedCourses(res.data || []);
    } catch (error) {
      toast.error('Failed to load trash');
    } finally {
      setTrashLoading(false);
    }
  };

  const handleRestore = async (course) => {
    setBusyTrashId(course.id);
    try {
      const headers = getAuthHeader();
      await axios.post(`${API_URL}/api/courses/${course.id}/restore`, {}, { headers });
      setTrashedCourses((prev) => prev.filter((c) => c.id !== course.id));
      // Also add the restored course back into the live lists so the user
      // sees it appear if they switch tabs.
      setMyCourses((prev) => prev.some((c) => c.id === course.id) ? prev : [...prev, course]);
      toast.success(`"${course.title}" restored.`);
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Failed to restore course';
      toast.error(detail);
    } finally {
      setBusyTrashId(null);
    }
  };

  const handlePurge = async (course) => {
    setBusyTrashId(course.id);
    try {
      const headers = getAuthHeader();
      await axios.post(`${API_URL}/api/courses/${course.id}/purge`, {}, { headers });
      setTrashedCourses((prev) => prev.filter((c) => c.id !== course.id));
      toast.success(`"${course.title}" permanently deleted.`);
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Failed to purge course';
      toast.error(detail);
    } finally {
      setBusyTrashId(null);
    }
  };

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    if (user) {
      fetchCourses();
    }
  }, [user, authLoading]);

  // Lazy-load the trash tray only when the user opens the Trash tab. Keeps
  // the initial /courses request fast and avoids surfacing deleted rows
  // to non-admin users we don't need to load.
  useEffect(() => {
    if (activeTab === 'trash' && user) {
      fetchTrash();
      clearSelection();
    }
    if (activeTab === 'archived' && user) {
      fetchArchived();
      clearSelection();
    }
  }, [activeTab, user]);

  const fetchCourses = async () => {
    try {
      const headers = getAuthHeader();
      const [coursesRes, progressRes] = await Promise.all([
        axios.get(`${API_URL}/api/courses`, { headers }),
        axios.get(`${API_URL}/api/progress`, { headers }).catch(() => ({ data: [] }))
      ]);
      setCourses(coursesRes.data);
      setMyProgress(progressRes.data);

      // Fetch my created courses if founder/admin
      if (user?.membership_tier === 'founder' || user?.is_admin) {
        const myCoursesRes = await axios.get(`${API_URL}/api/courses/my-courses`, { headers });
        setMyCourses(myCoursesRes.data);
      }

      // Lazily backfill AI thumbnails for courses missing one — throttled
      // so we don't hammer the LLM (max 3 in-flight at a time). Updates
      // the same courses/myCourses state as URLs stream in.
      backfillMissingThumbnails(coursesRes.data, headers);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const backfillMissingThumbnails = async (courseList, headers) => {
    const missing = (courseList || []).filter((c) => !c.thumbnail_url);
    if (missing.length === 0) return;
    // Simple concurrency limiter — 3 at a time, run silently in the background.
    const CONCURRENT = 3;
    const queue = [...missing];
    const workers = Array.from({ length: Math.min(CONCURRENT, queue.length) }, async () => {
      while (queue.length) {
        const course = queue.shift();
        if (!course) break;
        try {
          const res = await axios.post(
            `${API_URL}/api/courses/${course.id}/generate-thumbnail`,
            {},
            { headers }
          );
          if (res.data?.thumbnail_url) {
            setCourses((prev) => prev.map((c) => (c.id === course.id ? { ...c, thumbnail_url: res.data.thumbnail_url } : c)));
            setMyCourses((prev) => prev.map((c) => (c.id === course.id ? { ...c, thumbnail_url: res.data.thumbnail_url } : c)));
          }
        } catch (_e) {
          // Silent — placeholder is already shown; not blocking any UX.
        }
      }
    });
    await Promise.all(workers);
  };

  const categories = ['all', 'sales', 'marketing', 'leadership', 'finance', 'mindset', 'general'];

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         course.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || course.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // ---------- Featured + Discover split ----------
  // When no search/filter is active, surface up to 3 featured courses in a
  // hero row and 10 randomly-selected others below for discovery. When the
  // user is actively searching or filtering by category we collapse back to
  // a single flat grid so they see all matches.
  const isBrowsingHome = !searchQuery && selectedCategory === 'all';

  const featuredCourses = isBrowsingHome
    ? courses.filter((c) => c.is_featured).slice(0, 3)
    : [];

  // Distinct "deleted by" users present in the trash (admin filter options).
  const trashDeleters = Array.from(
    trashedCourses.reduce((map, c) => {
      if (c.deleted_by && !map.has(c.deleted_by)) {
        map.set(c.deleted_by, c.deleted_by_name || c.deleted_by_email || 'Unknown user');
      }
      return map;
    }, new Map())
  );
  const visibleTrashed = user?.is_admin && trashFilter !== 'all'
    ? trashedCourses.filter((c) => c.deleted_by === trashFilter)
    : trashedCourses;

  // Stable pseudo-random shuffle keyed by the page-load seed so the same
  // user sees the same order while on this page, but a different one on
  // next visit.
  const shuffledDiscover = (() => {
    if (!isBrowsingHome) return [];
    const featuredIds = new Set(featuredCourses.map((c) => c.id));
    const pool = courses.filter((c) => !featuredIds.has(c.id));
    // Fisher-Yates with a seeded RNG so the order is deterministic per load.
    const rng = mulberry32(Math.floor(shuffleSeed * 1e9));
    const arr = pool.slice();
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr.slice(0, 10);
  })();

  const getProgressForCourse = (courseId) => {
    return myProgress.find(p => p.course_id === courseId);
  };

  // Returns one of: 'creator' | 'admin' | 'free_for_tier' | 'free_course' | 'paid'
  const accessStateForCourse = (course) => {
    if (!user) return 'paid';
    if (course.creator_id === user.id) return 'creator';
    if (user.is_admin) return 'admin';
    const userTier = (user.membership_tier || 'none').toLowerCase();
    const freeTiers = (course.free_access_tiers || ['founder', 'growth']).map(t => String(t).toLowerCase());
    if (freeTiers.includes(userTier)) return 'free_for_tier';
    if ((course.price || 0) === 0) return 'free_course';
    return 'paid';
  };

  const canAccessCourse = (course) => accessStateForCourse(course) !== 'paid';

  // Cinematic featured hero — first featured course lit up like a MasterClass title card
  const heroCourse = featuredCourses[0] || courses[0] || null;

  const renderCinematicCard = (course, opts = {}) => {
    const { small = false } = opts;
    const progress = getProgressForCourse(course.id);
    const accessState = accessStateForCourse(course);
    const isPaid = accessState === 'paid';
    const isFreeForMe = ['free_for_tier', 'free_course', 'admin', 'creator'].includes(accessState);

    return (
      <Link
        to={`/courses/${course.id}`}
        key={course.id}
        className={`${small ? 'w-[240px]' : 'w-[320px]'} shrink-0 group cursor-pointer block`}
        data-testid="course-card"
      >
        <div className="relative w-full aspect-[4/5] rounded-xl overflow-hidden mb-3 bg-brand-surface border border-white/5 group-hover:border-brand-gold/30 transition-colors">
          {course.thumbnail_url ? (
            <img
              src={course.thumbnail_url}
              alt={course.title}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-brand-gold/10 to-brand-blue/10">
              <BookOpen className="w-16 h-16 text-brand-gold/40" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

          {/* Category badge */}
          <div className="absolute top-3 left-3">
            <span className="text-[10px] uppercase tracking-widest text-brand-gold font-bold bg-black/40 backdrop-blur px-2 py-1 rounded">
              {course.category || 'General'}
            </span>
          </div>

          {/* Access/price badge */}
          {isPaid ? (
            <div className="absolute top-3 right-3 px-2 py-1 rounded-full text-[10px] font-bold bg-brand-gold text-black flex items-center gap-1 shadow-lg" data-testid={`price-badge-${course.id}`}>
              <Lock className="w-3 h-3" /> ${course.price?.toFixed(2)}
            </div>
          ) : isFreeForMe && (course.price || 0) > 0 ? (
            <div className="absolute top-3 right-3 px-2 py-1 rounded-full text-[10px] font-bold bg-emerald-500/90 text-white" data-testid={`free-badge-${course.id}`}>FREE FOR YOU</div>
          ) : (
            <div className={`absolute top-3 right-3 px-2 py-1 rounded-full text-[10px] font-bold capitalize ${
              course.tier_required === 'founder' ? 'bg-brand-gold/90 text-black' :
              course.tier_required === 'growth' ? 'bg-brand-blue text-white' :
              'bg-white/10 text-white border border-white/20'
            }`} data-testid={`tier-badge-${course.id}`}>{course.tier_required}</div>
          )}

          {/* Play button on hover */}
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <button className="w-12 h-12 rounded-full border-2 border-white flex items-center justify-center text-white backdrop-blur-sm">
              <Play className="w-5 h-5 fill-current ml-0.5" />
            </button>
          </div>

          {/* Progress bar */}
          {progress && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
              <div className="h-full bg-brand-gold" style={{ width: `${progress.progress_percent}%` }} />
            </div>
          )}

          {/* Title on card bottom */}
          <div className="absolute bottom-3 left-3 right-3">
            <h3 className="font-playfair text-lg leading-snug text-white line-clamp-2">{course.title}</h3>
          </div>
        </div>
        <p className="text-xs text-gray-500 flex items-center gap-2">
          <Clock className="w-3 h-3" /> {course.total_duration_minutes || 0} min · {course.enrollment_count || 0} enrolled
        </p>
      </Link>
    );
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-gold mx-auto mb-4"></div>
          <p className="text-gray-400">Loading courses...</p>
        </div>
      </div>
    );
  }

  return (
    <AppShell active="courses" title="Learn">
      {/* Cinematic Hero (only on browse tab + when we have a hero) */}
      {activeTab === 'browse' && heroCourse && !searchQuery && selectedCategory === 'all' && (
        <div className="relative w-full h-[60vh] min-h-[420px] bg-black" data-testid="courses-hero">
          {heroCourse.thumbnail_url ? (
            <img src={heroCourse.thumbnail_url} alt={heroCourse.title} className="absolute inset-0 w-full h-full object-cover opacity-60" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-brand-gold/20 via-brand-blue/10 to-black" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-brand-bg/60 to-transparent" />

          <div className="absolute bottom-0 left-0 w-full p-8 md:p-16 z-10">
            <span className="text-brand-gold tracking-[0.2em] uppercase text-xs font-semibold mb-3 block">
              {heroCourse.is_featured ? 'Featured Class' : 'Recommended'}
            </span>
            <h1 className="text-4xl md:text-6xl font-playfair text-white mb-4 leading-tight max-w-3xl">{heroCourse.title}</h1>
            <p className="text-base md:text-lg text-gray-300 mb-6 max-w-2xl font-light line-clamp-3">{heroCourse.description}</p>
            <div className="flex items-center gap-3 flex-wrap">
              <Link to={`/courses/${heroCourse.id}`}>
                <Button className="bg-white text-black px-6 py-3 rounded-full font-semibold hover:bg-gray-200 flex items-center gap-2 shadow-lg" data-testid="hero-start-btn">
                  <Play className="w-5 h-5 fill-current" /> Start Trailer
                </Button>
              </Link>
              <Link to={`/courses/${heroCourse.id}`}>
                <Button variant="outline" className="bg-white/10 backdrop-blur-md border-white/20 text-white px-6 py-3 rounded-full font-medium hover:bg-white/20">
                  View Chapters
                </Button>
              </Link>
              {(user?.membership_tier === 'founder' || user?.is_admin) && (
                <Link to="/courses/create">
                  <Button className="bg-brand-gold text-black px-6 py-3 rounded-full font-semibold hover:brightness-110" data-testid="create-course-btn">
                    <Plus className="w-4 h-4 mr-2" /> Create Course
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Show page header only when not in browse-home mode */}
        {!(activeTab === 'browse' && heroCourse && !searchQuery && selectedCategory === 'all') && (
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div>
              <h1 className="font-playfair text-4xl md:text-5xl font-semibold text-white flex items-center gap-3">
                Learning Center
              </h1>
              <p className="text-sm text-gray-400 mt-1">Cinematic courses. Watch, learn, apply.</p>
            </div>
            {(user?.membership_tier === 'founder' || user?.is_admin) && (
              <Link to="/courses/create">
                <Button className="bg-brand-gold text-black hover:brightness-110 font-semibold" data-testid="create-course-btn">
                  <Plus className="w-4 h-4 mr-2" /> Create Course
                </Button>
              </Link>
            )}
          </div>
        )}

        {/* Tab pills */}
        <div className="flex gap-2 mb-6 flex-wrap" data-testid="course-tabs">
          <button
            onClick={() => setActiveTab('browse')}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${activeTab === 'browse' ? 'bg-white/10 text-white border border-white/20' : 'text-gray-400 hover:text-white'}`}
            data-testid="browse-tab"
          >All Classes</button>
          <button
            onClick={() => setActiveTab('progress')}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${activeTab === 'progress' ? 'bg-white/10 text-white border border-white/20' : 'text-gray-400 hover:text-white'}`}
            data-testid="progress-tab"
          >My Progress</button>
          {(user?.membership_tier === 'founder' || user?.is_admin) && (
            <>
              <button
                onClick={() => setActiveTab('created')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${activeTab === 'created' ? 'bg-brand-gold/20 text-brand-gold border border-brand-gold/30' : 'text-gray-400 hover:text-white'}`}
                data-testid="created-tab"
              >My Created Courses</button>
              <button
                onClick={() => setActiveTab('archived')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${activeTab === 'archived' ? 'bg-white/10 text-white border border-white/20' : 'text-gray-400 hover:text-white'}`}
                data-testid="archived-tab"
              >
                <Archive className="w-3.5 h-3.5" />
                Archived
              </button>
              <button
                onClick={() => setActiveTab('trash')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${activeTab === 'trash' ? 'bg-white/10 text-white border border-white/20' : 'text-gray-400 hover:text-white'}`}
                data-testid="trash-tab"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Trash
              </button>
            </>
          )}
        </div>

        {activeTab === 'browse' && (
          <>
            {/* Category chips + search */}
            <div className="flex items-center gap-3 mb-6 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    selectedCategory === cat ? 'bg-brand-gold/15 text-brand-gold border border-brand-gold/30' : 'text-gray-400 hover:text-white border border-transparent'
                  }`}
                  data-testid={`category-${cat}`}
                >{cat.charAt(0).toUpperCase() + cat.slice(1)}</button>
              ))}
              <div className="ml-auto relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search courses..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 rounded-full text-sm text-white w-64"
                  data-testid="course-search"
                />
              </div>
            </div>

            {/* Featured horizontal strip */}
            {isBrowsingHome && featuredCourses.length > 1 && (
              <div className="mb-10" data-testid="featured-courses-section">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-brand-gold" />
                  <h2 className="text-2xl font-playfair">Trending This Week</h2>
                </div>
                <div className="flex gap-4 overflow-x-auto pb-4" style={{ scrollbarWidth: 'none' }}>
                  {featuredCourses.slice(1).map((course) => renderCinematicCard(course))}
                </div>
              </div>
            )}

            {/* Discover / filtered grid */}
            {isBrowsingHome && (
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-5 h-5 text-brand-gold" />
                <h2 className="text-2xl font-playfair">Discover</h2>
                <span className="text-xs text-gray-500 ml-2">A fresh handful — refresh for more</span>
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="courses-grid">
              {(isBrowsingHome ? shuffledDiscover : filteredCourses).map((course) => renderCinematicCard(course, { small: true }))}
            </div>

            {(isBrowsingHome ? shuffledDiscover : filteredCourses).length === 0 && featuredCourses.length === 0 && (
              <div className="text-center py-16">
                <BookOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No courses found</h3>
                <p className="text-gray-400">Try adjusting your search or filters</p>
              </div>
            )}
          </>
        )}

        {activeTab === 'progress' && (
          <div className="space-y-6" data-testid="progress-panel">
            <h2 className="text-2xl font-playfair text-white">Your Learning Progress</h2>
            {myProgress.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-6">
                {myProgress.map((progress) => {
                  const course = courses.find((c) => c.id === progress.course_id);
                  if (!course) return null;
                  return (
                    <div key={progress.id} className="bg-brand-surface border border-white/5 rounded-2xl p-6" data-testid="progress-card">
                      <div className="flex gap-4">
                        <div className="w-24 h-24 rounded-lg bg-gray-800 overflow-hidden flex-shrink-0">
                          {course.thumbnail_url ? <img src={course.thumbnail_url} alt={course.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><BookOpen className="w-8 h-8 text-brand-gold/30" /></div>}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-white mb-1">{course.title}</h3>
                          <p className="text-xs text-gray-400 mb-3">{progress.completed_lessons?.length || 0} lessons completed</p>
                          <div className="w-full bg-white/10 rounded-full h-2 mb-2">
                            <div className="h-full bg-brand-gold rounded-full" style={{ width: `${progress.progress_percent}%` }} />
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-400">{Math.round(progress.progress_percent)}% complete</span>
                            {progress.certificate_id && <span className="text-emerald-400 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Certified</span>}
                          </div>
                        </div>
                      </div>
                      <Link to={`/courses/${course.id}`}>
                        <Button className="w-full mt-4 bg-brand-gold text-black hover:brightness-110">Continue Learning <ChevronRight className="w-4 h-4 ml-2" /></Button>
                      </Link>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16 bg-brand-surface border border-white/5 rounded-2xl">
                <GraduationCap className="w-16 h-16 text-brand-gold/30 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No courses started yet</h3>
                <p className="text-gray-400 mb-4">Start your learning journey today!</p>
                <Button onClick={() => setActiveTab('browse')} className="bg-brand-gold text-black hover:brightness-110">Browse Courses</Button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'created' && (
          <div className="space-y-6" data-testid="created-panel">
            <div className="flex justify-between items-center flex-wrap gap-3">
              <h2 className="text-2xl font-playfair text-white">My Created Courses</h2>
              <div className="flex items-center gap-2">
                {myCourses.length > 0 && (
                  <Button
                    variant="outline"
                    onClick={selectedIds.size === myCourses.length ? clearSelection : selectAllVisible}
                    className="border-white/10 text-white hover:bg-white/5 text-xs"
                    data-testid="bulk-select-all"
                  >
                    {selectedIds.size === myCourses.length ? 'Clear' : `Select all (${myCourses.length})`}
                  </Button>
                )}
                <Link to="/courses/create">
                  <Button className="bg-brand-gold text-black hover:brightness-110" data-testid="create-new-course-btn">
                    <Plus className="w-4 h-4 mr-2" /> Create New
                  </Button>
                </Link>
              </div>
            </div>

            {/* Bulk-select action bar — appears only when 1+ courses are selected */}
            {selectedIds.size > 0 && (
              <div
                className="flex items-center justify-between gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30"
                data-testid="bulk-action-bar"
              >
                <div className="text-sm text-white">
                  <strong>{selectedIds.size}</strong> course{selectedIds.size === 1 ? '' : 's'} selected
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={clearSelection}
                    className="border-white/10 text-white hover:bg-white/5 text-xs"
                    data-testid="bulk-cancel"
                  >
                    Cancel
                  </Button>
                  <AlertDialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
                    <AlertDialogTrigger asChild>
                      <Button
                        className="bg-amber-600 hover:bg-amber-700 text-white text-xs"
                        data-testid="bulk-archive-btn"
                      >
                        <Archive className="w-4 h-4 mr-1.5" />
                        Archive {selectedIds.size}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid="bulk-archive-dialog">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Archive {selectedIds.size} course{selectedIds.size === 1 ? '' : 's'}?</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                          <div className="space-y-2 text-sm">
                            <p>The following will be removed from every listing:</p>
                            <ul className="list-disc pl-5 space-y-0.5 text-muted-foreground max-h-40 overflow-y-auto">
                              {myCourses
                                .filter((c) => selectedIds.has(c.id))
                                .map((c) => (
                                  <li key={c.id} className="truncate">{c.title}</li>
                                ))}
                            </ul>
                            <p className="text-muted-foreground pt-2">
                              Nothing is deleted — archived courses keep all their content and sit safely
                              under the <strong>Archived</strong> tab. From there you can restore them or
                              move them to the Trash.
                            </p>
                          </div>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel disabled={bulkArchiving} data-testid="bulk-archive-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleBulkArchive}
                          disabled={bulkArchiving}
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                          data-testid="bulk-archive-confirm"
                        >
                          {bulkArchiving ? 'Archiving…' : `Archive ${selectedIds.size}`}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            )}

            {myCourses.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {myCourses.map((course) => {
                  const isSelected = selectedIds.has(course.id);
                  return (
                  <div
                    key={course.id}
                    className={`bg-brand-surface border rounded-2xl overflow-hidden transition-colors ${isSelected ? 'border-amber-500/60 ring-2 ring-amber-500/20' : 'border-white/5'}`}
                    data-testid="my-course-card"
                  >
                    {/* Bulk-select checkbox floating on the thumbnail */}
                    <div className="relative">
                      <div className="h-36 bg-gradient-to-br from-brand-gold/20 to-brand-surface overflow-hidden">
                        {course.thumbnail_url ? <img src={course.thumbnail_url} alt={course.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Crown className="w-12 h-12 text-brand-gold/40" /></div>}
                      </div>
                      <label
                        className="absolute top-3 left-3 bg-black/60 backdrop-blur-md rounded-md p-1.5 cursor-pointer hover:bg-black/80 transition-colors"
                        aria-label={`Select ${course.title} for bulk actions`}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSelect(course.id)}
                          className="border-white/60 data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
                          data-testid={`select-course-${course.id}`}
                        />
                      </label>
                    </div>
                    <div className="p-5">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold text-white">{course.title}</h3>
                        <span className={`text-xs px-2 py-1 rounded ${course.is_published ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'}`}>{course.is_published ? 'Published' : 'Draft'}</span>
                      </div>
                      <p className="text-xs text-gray-400 mb-4 line-clamp-2">{course.description}</p>
                      <div className="flex gap-2">
                        <Link to={`/courses/${course.id}/edit`} className="flex-1"><Button variant="outline" className="w-full border-white/10 text-white hover:bg-white/5" data-testid={`edit-course-${course.id}`}>Edit</Button></Link>
                        <Link to={`/courses/${course.id}`} className="flex-1"><Button className="w-full bg-brand-gold text-black hover:brightness-110" data-testid={`view-course-${course.id}`}>View</Button></Link>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300 px-3"
                              disabled={busyArchiveId === course.id}
                              data-testid={`archive-course-${course.id}`}
                              aria-label={`Archive ${course.title}`}
                              title="Archive course"
                            >
                              <Archive className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent data-testid="archive-course-dialog">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Archive &ldquo;{course.title}&rdquo;?</AlertDialogTitle>
                              <AlertDialogDescription asChild>
                                <div className="space-y-2 text-sm">
                                  <p>The course will be removed from every listing — nothing is deleted.</p>
                                  <p className="text-muted-foreground">
                                    It moves to the <strong>Archived</strong> tab where you can restore it
                                    anytime, or send it to the Trash from there.
                                  </p>
                                </div>
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel data-testid="archive-course-cancel">Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleArchiveCourse(course)}
                                className="bg-amber-600 hover:bg-amber-700 text-white"
                                data-testid="archive-course-confirm"
                              >
                                Archive course
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16 bg-brand-surface border border-white/5 rounded-2xl">
                <Crown className="w-16 h-16 text-brand-gold/40 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No courses created yet</h3>
                <p className="text-gray-400 mb-4">Share your expertise with the community!</p>
                <Link to="/courses/create"><Button className="bg-brand-gold text-black hover:brightness-110"><Plus className="w-4 h-4 mr-2" /> Create Your First Course</Button></Link>
              </div>
            )}
          </div>
        )}

        {activeTab === 'archived' && (
          <div className="space-y-6" data-testid="archived-panel">
            <div className="flex justify-between items-center flex-wrap gap-3">
              <div>
                <h2 className="text-2xl font-playfair text-white">Archived Courses</h2>
                <p className="text-sm text-gray-400 mt-1">
                  Hidden from all listings, kept forever. Restore anytime — or move to Trash to delete.
                </p>
              </div>
              <Button
                variant="outline"
                onClick={fetchArchived}
                disabled={archivedLoading}
                className="border-white/10 text-white hover:bg-white/5 text-xs"
                data-testid="archived-refresh"
              >
                {archivedLoading ? 'Loading…' : 'Refresh'}
              </Button>
            </div>

            {archivedLoading ? (
              <div className="text-center py-16 text-gray-400">Loading archived courses…</div>
            ) : archivedCourses.length === 0 ? (
              <div className="text-center py-16 bg-brand-surface border border-white/5 rounded-2xl" data-testid="archived-empty">
                <Archive className="w-16 h-16 text-brand-gold/30 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No archived courses</h3>
                <p className="text-gray-400">Archive a course from My Created Courses to park it here.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {archivedCourses.map((course) => (
                  <div
                    key={course.id}
                    className="bg-brand-surface border border-white/5 rounded-2xl overflow-hidden"
                    data-testid="archived-course-card"
                  >
                    <div className="h-36 bg-gradient-to-br from-amber-500/10 to-brand-surface overflow-hidden relative">
                      {course.thumbnail_url ? (
                        <img src={course.thumbnail_url} alt={course.title} className="w-full h-full object-cover opacity-80" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Archive className="w-12 h-12 text-amber-500/40" />
                        </div>
                      )}
                      <span className="absolute top-3 right-3 text-xs px-2 py-1 rounded bg-black/70 backdrop-blur-md text-amber-300 border border-amber-500/30">
                        Archived
                      </span>
                    </div>
                    <div className="p-5">
                      <h3 className="font-semibold text-white mb-1 line-clamp-1">{course.title}</h3>
                      <p className="text-xs text-gray-400 mb-4 line-clamp-2">{course.description}</p>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleUnarchive(course)}
                          disabled={busyArchiveId === course.id}
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                          data-testid={`unarchive-course-${course.id}`}
                        >
                          <ArchiveRestore className="w-3.5 h-3.5 mr-1.5" />
                          {busyArchiveId === course.id ? 'Restoring…' : 'Restore'}
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              disabled={deletingCourseId === course.id}
                              className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 px-3 text-xs"
                              data-testid={`trash-course-${course.id}`}
                              aria-label={`Move ${course.title} to trash`}
                              title="Move to trash"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent data-testid={`trash-dialog-${course.id}`}>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Move &ldquo;{course.title}&rdquo; to trash?</AlertDialogTitle>
                              <AlertDialogDescription asChild>
                                <div className="space-y-2 text-sm">
                                  <p>
                                    It will sit in the Trash for <strong className="text-amber-400">30 days</strong> —
                                    restore it there anytime before it&apos;s permanently deleted.
                                  </p>
                                </div>
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel data-testid="trash-course-cancel">Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteCourse(course)}
                                className="bg-red-600 hover:bg-red-700 text-white"
                                data-testid="trash-course-confirm"
                              >
                                Move to trash
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'trash' && (
          <div className="space-y-6" data-testid="trash-panel">
            <div className="flex justify-between items-center flex-wrap gap-3">
              <div>
                <h2 className="text-2xl font-playfair text-white">Trash</h2>
                <p className="text-sm text-gray-400 mt-1">
                  Deleted courses live here for 30 days. Restore in one click — or empty this row now.
                </p>
              </div>
              <div className="flex gap-2 items-center flex-wrap">
                {user?.is_admin && trashedCourses.length > 0 && trashDeleters.length > 0 && (
                  <Select value={trashFilter} onValueChange={setTrashFilter}>
                    <SelectTrigger
                      className="w-[210px] bg-white/5 border-white/10 text-white text-xs h-9"
                      data-testid="trash-owner-filter"
                    >
                      <SelectValue placeholder="Filter by who deleted" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" data-testid="trash-filter-all">
                        All users ({trashedCourses.length})
                      </SelectItem>
                      {trashDeleters.map(([id, label]) => (
                        <SelectItem key={id} value={id} data-testid={`trash-filter-${id}`}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Button
                  variant="outline"
                  onClick={fetchTrash}
                  disabled={trashLoading}
                  className="border-white/10 text-white hover:bg-white/5 text-xs"
                  data-testid="trash-refresh"
                >
                  {trashLoading ? 'Loading…' : 'Refresh'}
                </Button>
                {visibleTrashed.length > 0 && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        disabled={emptyingTrash}
                        className="bg-red-600 hover:bg-red-700 text-white text-xs"
                        data-testid="empty-trash-btn"
                      >
                        <XSquare className="w-3.5 h-3.5 mr-1.5" />
                        {emptyingTrash
                          ? 'Emptying…'
                          : user?.is_admin && trashFilter !== 'all'
                            ? `Empty shown (${visibleTrashed.length})`
                            : `Empty Trash (${trashedCourses.length})`}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid="empty-trash-dialog">
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          {user?.is_admin && trashFilter !== 'all' ? 'Empty this user\u2019s trash?' : 'Empty the entire trash?'}
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                          <div className="space-y-2 text-sm">
                            <p>
                              <strong>{visibleTrashed.length}</strong> trashed course{visibleTrashed.length === 1 ? '' : 's'} —
                              including modules, lessons, quizzes and learner progress — will be wiped immediately.
                            </p>
                            <p className="text-red-400 font-medium pt-2">This can&apos;t be undone.</p>
                          </div>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="empty-trash-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleEmptyTrash}
                          className="bg-red-600 hover:bg-red-700 text-white"
                          data-testid="empty-trash-confirm"
                        >
                          Empty trash
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </div>

            {trashLoading ? (
              <div className="text-center py-16 text-gray-400">Loading trash…</div>
            ) : trashedCourses.length === 0 ? (
              <div className="text-center py-16 bg-brand-surface border border-white/5 rounded-2xl" data-testid="trash-empty">
                <Trash2 className="w-16 h-16 text-brand-gold/30 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Trash is empty</h3>
                <p className="text-gray-400">
                  When you delete a course it will land here for 30 days.
                </p>
              </div>
            ) : visibleTrashed.length === 0 ? (
              <div className="text-center py-10 text-gray-400" data-testid="trash-filter-empty">
                No trashed courses from this user.
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visibleTrashed.map((course) => {
                  // Days remaining before auto-purge
                  let daysLeft = null;
                  if (course.purges_at) {
                    const diff = new Date(course.purges_at).getTime() - Date.now();
                    daysLeft = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
                  }
                  return (
                    <div
                      key={course.id}
                      className="bg-brand-surface border border-white/5 rounded-2xl overflow-hidden opacity-90"
                      data-testid="trashed-course-card"
                    >
                      <div className="h-36 bg-gradient-to-br from-red-500/10 to-brand-surface overflow-hidden relative">
                        {course.thumbnail_url ? (
                          <img src={course.thumbnail_url} alt={course.title} className="w-full h-full object-cover grayscale opacity-70" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Trash2 className="w-12 h-12 text-red-500/40" />
                          </div>
                        )}
                        {daysLeft !== null && (
                          <span className="absolute top-3 right-3 text-xs px-2 py-1 rounded bg-black/70 backdrop-blur-md text-amber-300 border border-amber-500/30">
                            {daysLeft === 0 ? 'Purges today' : daysLeft === 1 ? '1 day left' : `${daysLeft} days left`}
                          </span>
                        )}
                      </div>
                      <div className="p-5">
                        <h3 className="font-semibold text-white mb-1 line-clamp-1">{course.title}</h3>
                        <p className="text-xs text-gray-400 mb-2 line-clamp-2">{course.description}</p>
                        {user?.is_admin && (course.deleted_by_name || course.deleted_by_email) && (
                          <p className="text-[11px] text-gray-500 mb-3" data-testid={`deleted-by-${course.id}`}>
                            Deleted by <span className="text-gray-300">{course.deleted_by_name || course.deleted_by_email}</span>
                          </p>
                        )}
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleRestore(course)}
                            disabled={busyTrashId === course.id}
                            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                            data-testid={`restore-course-${course.id}`}
                          >
                            <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                            {busyTrashId === course.id ? 'Restoring…' : 'Restore'}
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="outline"
                                disabled={busyTrashId === course.id}
                                className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 px-3 text-xs"
                                data-testid={`purge-course-${course.id}`}
                                aria-label={`Permanently delete ${course.title}`}
                                title="Delete permanently"
                              >
                                <XSquare className="w-3.5 h-3.5" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent data-testid={`purge-dialog-${course.id}`}>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Permanently delete &ldquo;{course.title}&rdquo;?</AlertDialogTitle>
                                <AlertDialogDescription asChild>
                                  <div className="space-y-2 text-sm">
                                    <p>This skips the 30-day auto-purge and wipes the course, its modules, lessons, quizzes and all learner progress right now.</p>
                                    <p className="text-red-400 font-medium pt-2">This can&apos;t be undone.</p>
                                  </div>
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel data-testid="purge-cancel">Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handlePurge(course)}
                                  className="bg-red-600 hover:bg-red-700 text-white"
                                  data-testid="purge-confirm"
                                >
                                  Delete forever
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}
